import { useEffect } from 'react';
import ROUTES from '../../config/Routes';

export const ADMIN_TABS_ID = 'admin-settings-tabs';

const MessageEventDataType = {
  ELEMENT_FOUND: 'element-found',
  ELEMENT_STATUS: 'element-status',
  REQUEST_STATUS: 'request-status',
};

export const getAdminSettingsBaseRoute = () =>
  window.location.toString().includes(ROUTES.ADMIN_EMBEDDABLE)
    ? ROUTES.ADMIN_EMBEDDABLE
    : ROUTES.ADMIN;

/**
 * useIframeAnnounce : Permet de poster un message lorsque qu'un élément avec l'id donné apparait dans le DOM
 *  → est notamment utilisé dans SIG pour détecter que l'Iframe se charge bien
 * — targetId : id de l'élément à surveiller
 * — allowedParentOrigins : liste des origines parentes autorisées à recevoir les messages
 */
export function useIframeAnnounce(targetId, allowedParentOrigins) {
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const parentOriginParam = params.get('parentOrigin');

    if (
      !allowedParentOrigins ||
      !targetId ||
      !parentOriginParam ||
      allowedParentOrigins.length === 0 ||
      targetId.length === 0 ||
      parentOriginParam.length === 0
    ) {
      console.warn(
        'useIframeAnnounce: allowedParentOrigins, parentOrigin ou targetId manquant. postMessage désactivé.'
      );
      return;
    }

    if (!allowedParentOrigins.includes(parentOriginParam)) {
      console.warn(
        `useIframeAnnounce: L'origine parente "${parentOriginParam}" n'est pas autorisée. postMessage désactivé.`
      );
      return;
    }

    // Envoyer un message au parent
    const notifyParent = (type, data) => {
      try {
        window.parent.postMessage(
          { type, targetId, ...data },
          parentOriginParam
        );
      } catch (e) {
        console.warn('postMessage failed:', e);
      }
    };

    // Notifier si l'élément existe
    const notifyIfTargetExists = () => {
      const el = document.getElementById(targetId);
      if (el) {
        notifyParent(MessageEventDataType.ELEMENT_FOUND, {
          text: el.textContent ?? null,
        });
        return true;
      }
      return false;
    };

    // Vérification initiale
    if (notifyIfTargetExists()) {
      return;
    }

    // Observer les mutations pour détecter l'apparition de l'élément
    const observer = new MutationObserver(() => {
      if (notifyIfTargetExists()) {
        observer.disconnect();
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    // Écouter les requêtes du parent
    const onMessage = (event) => {
      if (
        event.origin !== parentOriginParam ||
        event.data?.type !== MessageEventDataType.REQUEST_STATUS ||
        event.data?.id !== targetId
      ) {
        return;
      }

      const el = document.getElementById(targetId);
      notifyParent(MessageEventDataType.ELEMENT_STATUS, {
        id: targetId,
        exists: !!el,
        text: el?.textContent ?? null,
      });
    };

    window.addEventListener('message', onMessage);

    return () => {
      observer.disconnect();
      window.removeEventListener('message', onMessage);
    };
  }, [targetId, allowedParentOrigins]);
}
